import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TSelectData } from '@components/formFields/select/types'
import { getDatasetsQuery } from '@global/api'
import { KEY } from '@global/hooks/queries/useListQuery'
import { TListTypes, LIST_TYPES, ListDTOResponse } from '@global/types'
import { useAppDispatch } from '@redux/hooks'
import { ApiResponseTemplate } from '@templates/types'
import { tempIdGenerator } from '@utils/temp-id-generator'
import { showSnackbar } from 'functions'
import { getSelectedColumns } from './utils'
import { t } from '@lingui/core/macro'
import { AxiosError } from 'axios'

interface Args {
  listType: TListTypes
  templateColumns?: ApiResponseTemplate | null
  selectedTemplateColumn: TSelectData | null
  selectedTemplateID: string | null
  afterSetPoweredListCallback?: () => void
}

interface IMutationFn {
  signal?: AbortSignal
  listId: string
  select: string[]
  errorCallback: (error: Error) => void
  templateRollupDatasetId: string
}

/**
 * Sets the lists to be powered by the selected template, but does NOT save the changes.
 * Mutates the list in the cache to reflect the powered list state; so that you can preview
 * changes before they are saved.
 */
export const useSavePowered = ({
  listType,
  templateColumns,
  selectedTemplateColumn,
  selectedTemplateID,
  afterSetPoweredListCallback,
}: Args) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const postQueryMutation = useMutation({
    mutationFn: ({ signal, select, errorCallback, templateRollupDatasetId }: IMutationFn) =>
      getDatasetsQuery({
        datasetId: templateRollupDatasetId,
        queryOptions: { limit: 100, commitRef: 'HEAD', distinct: true, select },
        axiosOptions: { signal },
        errorCallback,
      }).catch((error: unknown) => {
        // we have cases where the server returns 404, but is intended
        // so we swallow the error on the FE as not to disrupt the user experience
        // TODO: error handling the FE/BE contract needs an overhaul
        if (!(error instanceof AxiosError) || error.status !== 404) {
          throw error
        }
      }),
  })

  const setPoweredList = useCallback(
    (listId: string) => {
      const templateRollupDatasetId = templateColumns?.rollup_dataset_id

      if (!templateColumns || !templateRollupDatasetId) {
        dispatch(
          showSnackbar({
            message: t`Something went wrong. We could not power the list. Please try again.`,
            type: 'ERROR',
            duration: 5000,
          }),
        )

        return
      }

      const removeGovernanceTypeCVIDField = tf => tf.field?.governance_type !== 'cv_id'
      const templateFields = templateColumns?.template_fields?.filter(removeGovernanceTypeCVIDField)
      const select = getSelectedColumns({ listType, selectedTemplateColumn, templateFields })

      postQueryMutation.mutate(
        {
          listId,
          select,
          templateRollupDatasetId: String(templateRollupDatasetId),
          errorCallback: error => {
            dispatch(
              showSnackbar({
                message: error.message,
                type: 'ERROR',
                duration: 5000,
              }),
            )
          },
        },
        {
          onSuccess: (query, variables) => {
            queryClient.setQueryData<ListDTOResponse>([KEY, variables.listId], prevList => {
              if (!prevList) {
                dispatch(
                  showSnackbar({
                    message: t`Something went wrong. We could not power the list. Please try again.`,
                    type: 'ERROR',
                    duration: 5000,
                  }),
                )
                return
              }

              const listValues = (fieldId: number | undefined) => {
                if (fieldId && query?.rows) {
                  return query.rows.map((row, idx) => ({
                    row_number: idx,
                    value: row.data[fieldId]?.value,
                    id: row.data[fieldId]?.id,
                  }))
                }

                return [{ row_number: 1, value: null, id: null }]
              }

              if (listType === LIST_TYPES.dropDown) {
                const tmplField = templateFields.find(
                  tf => tf.id === selectedTemplateColumn?.id,
                )?.field
                return {
                  ...prevList,
                  powered_template_id: selectedTemplateID || null,
                  list_columns: [
                    {
                      id: prevList.list_columns[0]?.id ?? tempIdGenerator('lc'),
                      name: tmplField?.name ?? '',
                      precedence: 0,
                      powered_template_field_id: selectedTemplateColumn?.id || null,
                      list_values: listValues(tmplField?.id),
                      created_at: '',
                      created_by_id: null,
                      list_id: -1,
                      nullable: null,
                      require_unique: null,
                      updated_at: '',
                      updated_by_id: -1,
                    },
                  ],
                }
              }

              return {
                ...prevList,
                powered_template_id: selectedTemplateColumn?.id || null,
                list_columns: templateFields.map((tmplField, index) => ({
                  id: prevList.list_columns[index]?.id || tempIdGenerator('lc'),
                  name: tmplField?.field?.name || '',
                  powered_template_field_id: tmplField?.id || null,
                  precedence: index,
                  list_values: listValues(tmplField?.field?.id),
                  created_at: '',
                  created_by_id: null,
                  list_id: -1,
                  nullable: null,
                  require_unique: null,
                  updated_at: '',
                  updated_by_id: -1,
                })),
              }
            })
          },
          onError: error => {
            dispatch(
              showSnackbar({
                message: `${t`Something went wrong. We could not power the list.`} ${error}`,
                type: 'ERROR',
                duration: 5000,
              }),
            )
          },
          onSettled: () => {
            afterSetPoweredListCallback?.()
          },
        },
      )
    },
    [templateColumns, selectedTemplateColumn, listType, selectedTemplateID],
  )

  const unPowerList = useCallback((listId: string) => {
    queryClient.setQueryData<ListDTOResponse>([KEY, listId], prevList => {
      if (!prevList || !listId) {
        dispatch(
          showSnackbar({
            message: t`Something went wrong. We could not un-power the list. Please try again.`,
            type: 'ERROR',
            duration: 5000,
          }),
        )

        return
      }

      return {
        ...prevList,
        powered_template_id: null,
        list_columns: [
          {
            id: tempIdGenerator('lc'),
            name: '',
            powered_template_field_id: null,
            precedence: 0,
            list_values: [{ row_number: 0, value: null, id: tempIdGenerator('lv') }],
            created_at: '',
            created_by_id: null,
            list_id: -1,
            nullable: null,
            require_unique: null,
            updated_at: '',
            updated_by_id: -1,
          },
        ],
      }
    })
  }, [])

  return {
    setPoweredList,
    unPowerList,
    isSettingPoweredList: postQueryMutation.isPending && !postQueryMutation.isSuccess,
  }
}
